import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

// Pregunta frecuente de la tienda (tabla `faqs`).
export interface Faq {
  id: string;
  question: string;
  answer: string;
  category: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

// Contenido de respaldo: se usa si la tabla está vacía o sin conexión, para
// que la sección del home nunca aparezca en blanco.
export const DEFAULT_FAQS: Omit<Faq, 'id' | 'createdAt'>[] = [
  {
    question: '¿Qué garantía tienen las babuchas?',
    answer:
      'Todas nuestras paticas cuentan con garantía por defectos de fabricación de 90 días desde la fecha de compra. Si tu par presenta un problema cubierto, lo cambiamos o reparamos sin costo.',
    category: 'Garantías',
    displayOrder: 1,
    isActive: true,
  },
  {
    question: '¿Cómo funcionan los envíos contraentrega con Interrapidísimo?',
    answer:
      'Despachamos a todo el país por Interrapidísimo. Con el pago contra entrega, cancelas en efectivo al recibir tu pedido. Te compartimos la guía por WhatsApp al despachar.',
    category: 'Envíos Contraentrega',
    displayOrder: 2,
    isActive: true,
  },
  {
    question: '¿Qué pasa si pido una talla o referencia agotada?',
    answer:
      'Si tu talla o referencia no tiene stock, la mandamos a hacer especialmente para ti. Los pedidos de producción se entregan en 5 a 6 días hábiles y te contamos el avance por WhatsApp.',
    category: 'Pedidos Fuera de Stock',
    displayOrder: 3,
    isActive: true,
  },
  {
    question: '¿Puedo cambiar o devolver mi pedido?',
    answer:
      'Sí. Tienes hasta 5 días calendario desde la entrega para cambios o devoluciones, si el producto está sin uso con su empaque original. Escríbenos por WhatsApp y coordinamos todo.',
    category: 'Cambios y Devoluciones',
    displayOrder: 4,
    isActive: true,
  },
];

function mapRow(row: Record<string, any>): Faq {
  return {
    id: row.id,
    question: row.question ?? '',
    answer: row.answer ?? '',
    category: row.category ?? 'General',
    displayOrder: Number(row.display_order ?? 0),
    isActive: row.is_active ?? true,
    createdAt: row.created_at ?? '',
  };
}

// Preguntas activas para la tienda pública (ordenadas). Si no hay filas,
// devuelve el contenido por defecto para no dejar la sección vacía.
export async function fetchPublicFaqs(): Promise<Faq[]> {
  if (!isSupabaseConfigured || !supabase) {
    return DEFAULT_FAQS.map((f, i) => ({ ...f, id: `default-${i}`, createdAt: '' }));
  }
  try {
    const { data, error } = await supabase
      .from('faqs')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Array<Record<string, any>>;
    if (rows.length === 0) {
      return DEFAULT_FAQS.map((f, i) => ({ ...f, id: `default-${i}`, createdAt: '' }));
    }
    return rows.map(mapRow);
  } catch (err) {
    console.warn('[faqs] public fetch failed:', err);
    return DEFAULT_FAQS.map((f, i) => ({ ...f, id: `default-${i}`, createdAt: '' }));
  }
}

// Todas las preguntas (incluye inactivas) para el panel de administración.
export async function fetchAllFaqs(): Promise<Faq[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('faqs')
    .select('*')
    .order('display_order', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, any>>).map(mapRow);
}

export interface FaqInput {
  id?: string;
  question: string;
  answer: string;
  category: string;
  displayOrder: number;
  isActive: boolean;
}

export async function saveFaq(input: FaqInput): Promise<{ error: string | null }> {
  if (!(await canWrite('faqs'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  if (!input.question.trim() || !input.answer.trim()) {
    return { error: 'La pregunta y la respuesta son obligatorias.' };
  }

  const payload = {
    question: input.question.trim(),
    answer: input.answer.trim(),
    category: input.category.trim() || 'General',
    display_order: input.displayOrder,
    is_active: input.isActive,
  };

  try {
    if (input.id) {
      const { error } = await supabase.from('faqs').update(payload).eq('id', input.id);
      if (error) return { error: error.message };
      recordAudit({ entityType: 'settings', entityId: input.id, action: 'UPDATE', newValue: payload.question });
    } else {
      const { data, error } = await supabase.from('faqs').insert(payload).select('id');
      if (error) return { error: error.message };
      recordAudit({
        entityType: 'settings',
        entityId: (data?.[0] as { id: string } | undefined)?.id ?? '',
        action: 'CREATE',
        newValue: payload.question,
      });
    }
    return { error: null };
  } catch (err) {
    console.warn('[faqs] save failed:', err);
    return { error: 'Error de conexión al guardar la pregunta.' };
  }
}

export async function deleteFaq(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('faqs'))) return { error: DELETE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  try {
    const { error } = await supabase.from('faqs').delete().eq('id', id);
    if (error) return { error: error.message };
    recordAudit({ entityType: 'settings', entityId: id, action: 'DELETE' });
    return { error: null };
  } catch (err) {
    console.warn('[faqs] delete failed:', err);
    return { error: 'Error de conexión al eliminar la pregunta.' };
  }
}

// Reordena: persiste display_order según el orden recibido.
export async function reorderFaqs(orderedIds: string[]): Promise<{ error: string | null }> {
  if (!(await canWrite('faqs'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  try {
    await Promise.all(
      orderedIds.map((id, index) =>
        supabase.from('faqs').update({ display_order: index + 1 }).eq('id', id),
      ),
    );
    return { error: null };
  } catch (err) {
    console.warn('[faqs] reorder failed:', err);
    return { error: 'Error de conexión al reordenar.' };
  }
}